import { Link } from "react-router-dom";
import { CheckCircle2Icon, LoaderIcon, MoreVerticalIcon, PlusIcon } from "lucide-react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

export function DashboardDataTable({
  organizations = [],
  roles = {},
}) {
  const roleEntries = Object.entries(roles);

  return (
    <Tabs defaultValue="organizations" className="flex w-full flex-col justify-start gap-6">
      <div className="flex items-center justify-between px-4 lg:px-6">
        <TabsList className="hidden @4xl/main:flex">
          <TabsTrigger value="organizations">Organizations</TabsTrigger>
          <TabsTrigger value="roles">
            Roles{" "}
            <Badge
              variant="secondary"
              className="flex h-5 w-5 items-center justify-center rounded-full bg-muted-foreground/30"
            >
              {roleEntries.length}
            </Badge>
          </TabsTrigger>
          <TabsTrigger value="status">Platform</TabsTrigger>
        </TabsList>
        <TabsList className="@4xl/main:hidden">
          <TabsTrigger value="organizations">Orgs</TabsTrigger>
          <TabsTrigger value="roles">Roles</TabsTrigger>
          <TabsTrigger value="status">Status</TabsTrigger>
        </TabsList>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" asChild>
            <Link to="/admin/agencies">
              Manage
            </Link>
          </Button>
          <Button size="sm" asChild>
            <Link to="/admin/agencies/new">
              <PlusIcon />
              <span className="hidden lg:inline">Register Org</span>
            </Link>
          </Button>
        </div>
      </div>

      <TabsContent
        value="organizations"
        className="relative flex flex-col gap-4 overflow-auto px-4 lg:px-6"
      >
        <div className="overflow-hidden rounded-lg border">
          <Table>
            <TableHeader className="bg-muted sticky top-0 z-10">
              <TableRow>
                <TableHead>Organization</TableHead>
                <TableHead>Timezone</TableHead>
                <TableHead>Staff</TableHead>
                <TableHead>Clients</TableHead>
                <TableHead>Visits</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="w-10" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {organizations.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} className="h-24 text-center text-muted-foreground">
                    No organizations yet. Register your first tenant to get started.
                  </TableCell>
                </TableRow>
              ) : (
                organizations.map((agency) => (
                  <TableRow key={agency.id}>
                    <TableCell>
                      <Link
                        to={`/admin/agencies/${agency.id}`}
                        className="font-medium no-underline hover:underline"
                      >
                        {agency.name}
                      </Link>
                      <div className="text-xs text-muted-foreground">{agency.slug}</div>
                    </TableCell>
                    <TableCell>{agency.timezone}</TableCell>
                    <TableCell>{agency.user_count}</TableCell>
                    <TableCell>{agency.client_count}</TableCell>
                    <TableCell>{agency.visit_count}</TableCell>
                    <TableCell>
                      <Badge
                        variant="outline"
                        className="flex w-fit gap-1 px-1.5 text-muted-foreground [&_svg]:size-3"
                      >
                        {agency.is_active ? (
                          <CheckCircle2Icon className="text-emerald-600" />
                        ) : (
                          <LoaderIcon />
                        )}
                        {agency.is_active ? "Active" : "Suspended"}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button
                            variant="ghost"
                            className="flex size-8 text-muted-foreground data-[state=open]:bg-muted"
                            size="icon"
                          >
                            <MoreVerticalIcon />
                            <span className="sr-only">Open menu</span>
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="w-40">
                          <DropdownMenuItem asChild>
                            <Link to={`/admin/agencies/${agency.id}`}>Edit</Link>
                          </DropdownMenuItem>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem asChild>
                            <Link to="/admin/agencies">View all</Link>
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      </TabsContent>

      <TabsContent value="roles" className="flex flex-col px-4 lg:px-6">
        <div className="overflow-hidden rounded-lg border">
          <Table>
            <TableHeader className="bg-muted">
              <TableRow>
                <TableHead>Role</TableHead>
                <TableHead>Users</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {roleEntries.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={2} className="h-24 text-center text-muted-foreground">
                    No role assignments yet.
                  </TableCell>
                </TableRow>
              ) : (
                roleEntries.map(([role, count]) => (
                  <TableRow key={role}>
                    <TableCell className="capitalize font-medium">{role}</TableCell>
                    <TableCell>{count}</TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      </TabsContent>

      <TabsContent value="status" className="flex flex-col gap-3 px-4 text-sm lg:px-6">
        <div className="rounded-lg border p-4">
          <div className="font-medium">All systems operational</div>
          <p className="mt-1 text-muted-foreground">
            Modular monolith · PostgreSQL / SQLite · Session CSRF auth · Agency-scoped RBAC
          </p>
        </div>
      </TabsContent>
    </Tabs>
  );
}
